import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { HandlerEnv } from './placement/handlers';
import { AVATAR_NAME_MAX, type GameSave } from '@/core/persistence/schema/v1';

/** A display name as stored: control characters dropped, spaces collapsed, trimmed; empty → no name. */
export function cleanAvatarName(raw: string): string | undefined {
  // eslint-disable-next-line no-control-regex
  const visible = raw.replace(/[\u0000-\u001f\u007f]/g, '');
  const name = visible.replace(/\s+/g, ' ').trim();
  return name === '' ? undefined : name;
}

/**
 * S20 Hồ sơ (content-schemas §13 `setAvatar`): change hair / shirt colour / hat / frame / name. Every part must be
 * owned and sit in its own slot; the frame may be cleared; the name is cleaned and at most AVATAR_NAME_MAX
 * characters (empty clears it). Applied (and saved) immediately.
 */
export function setAvatar(draft: GameSave, cmd: CommandOf<'setAvatar'>, env: HandlerEnv): CommandResult {
  const { name: rawName, ...parts } = cmd.patch;
  const next = { ...draft.avatar, ...parts };
  if ('name' in cmd.patch) {
    const name = rawName === undefined ? undefined : cleanAvatarName(rawName);
    if (name !== undefined && name.length > AVATAR_NAME_MAX)
      return { ok: false, error: 'INVALID_ARGUMENT', detail: { name: 'too_long' } };
    if (name === undefined) delete next.name;
    else next.name = name;
  }
  for (const slot of ['hair', 'shirtColor', 'hat'] as const) {
    const id = next[slot];
    const def = env.content.cosmetics.avatarParts.find((p) => p.id === id);
    if (def?.slot !== slot) return { ok: false, error: 'INVALID_ARGUMENT', detail: { slot } };
    if (!draft.cosmetics.avatarParts.includes(id))
      return { ok: false, error: 'LOCKED', detail: { partId: id } };
  }
  if (next.frame !== null && !draft.cosmetics.avatarFrames.includes(next.frame))
    return { ok: false, error: 'LOCKED', detail: { frameId: next.frame } };
  draft.avatar = next;
  return { ok: true, events: [] };
}

/** The title shown under the avatar (Menu, S20). Only owned titles; null clears it. */
export function equipTitle(draft: GameSave, cmd: CommandOf<'equipTitle'>): CommandResult {
  if (cmd.titleId !== null && !draft.cosmetics.titles.includes(cmd.titleId))
    return { ok: false, error: 'LOCKED', detail: { titleId: cmd.titleId } };
  draft.cosmetics.equippedTitle = cmd.titleId;
  return { ok: true, events: [] };
}
